import { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Header from '../../../../components/Header';
import { useCourses } from '../../hooks/useCourses';
import { uploadToCloudinary, shouldUseCloudinary } from '../../../../lib/cloudinaryUpload';
import { retryUpload } from '../../../../lib/retryUpload';
import { CourseLayerContent } from '../../../../types/course';

// ── Types ────────────────────────────────────────────────────────────

interface CapsuleWorkingState {
  id: string;
  slug: string;
  title: string;
  artist: string;
  introDescription: string;
  introVideoFile: File | null;
  introVideoFileName: string;
  existingIntroVideoUrl: string;
  chordsVideoFile: File | null;
  chordsVideoFileName: string;
  existingChordsVideoUrl: string;
  // Practice materials
  melodyAudioFile: File | null;
  melodyAudioFileName: string;
  existingMelodyAudioUrl: string;
  chordsPdfFile: File | null;
  chordsPdfFileName: string;
  existingChordsPdfUrl: string;
}

type WizardStep = 'metadata' | 'capsules' | 'edit-capsule' | 'preview';

const STEPS: { key: WizardStep; label: string }[] = [
  { key: 'metadata', label: 'Course Info' },
  { key: 'capsules', label: 'Lessons' },
  { key: 'preview', label: 'Preview & Save' },
];

const HEBREW_MAP: Record<string, string> = {
  'א': 'a', 'ב': 'b', 'ג': 'g', 'ד': 'd', 'ה': 'h', 'ו': 'v',
  'ז': 'z', 'ח': 'ch', 'ט': 't', 'י': 'y', 'כ': 'k', 'ך': 'k',
  'ל': 'l', 'מ': 'm', 'ם': 'm', 'נ': 'n', 'ן': 'n', 'ס': 's',
  'ע': 'a', 'פ': 'p', 'ף': 'f', 'צ': 'tz', 'ץ': 'tz', 'ק': 'k',
  'ר': 'r', 'ש': 'sh', 'ת': 't',
};

function slugify(text: string): string {
  // Transliterate Hebrew characters, keep ASCII as-is
  const transliterated = Array.from(text.toLowerCase())
    .map((ch) => HEBREW_MAP[ch] || ch)
    .join('');

  // Now clean up to a URL-safe slug
  const slug = transliterated
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();

  return slug || `capsule-${Date.now()}`;
}

function generateId(): string {
  return `cap_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

// ── Component ────────────────────────────────────────────────────────

export function CourseCurationTool() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getCourseBySlug, refetch } = useCourses();

  const editingSlug = searchParams.get('course');
  const existingCourse = editingSlug ? getCourseBySlug(editingSlug) : undefined;

  // Course-level state
  const [courseTitle, setCourseTitle] = useState('');
  const [courseSlug, setCourseSlug] = useState('');
  const [courseArtist, setCourseArtist] = useState('');
  const [courseDescription, setCourseDescription] = useState('');
  const [slugLocked, setSlugLocked] = useState(false);

  // Capsule working state
  const [capsules, setCapsules] = useState<CapsuleWorkingState[]>([]);
  const [editingCapsuleIndex, setEditingCapsuleIndex] = useState<number | null>(null);

  // Wizard
  const [step, setStep] = useState<WizardStep>('metadata');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});

  // Load existing course data
  useEffect(() => {
    if (existingCourse) {
      setCourseTitle(existingCourse.title);
      setCourseSlug(existingCourse.slug);
      setCourseArtist(existingCourse.artist || '');
      setCourseDescription(existingCourse.description || '');
      setSlugLocked(true);
      setCapsules(
        existingCourse.capsules.map((c) => ({
          id: c.id,
          slug: c.slug,
          title: c.title,
          artist: c.artist || '',
          introDescription: c.layers.intro?.description || '',
          introVideoFile: null,
          introVideoFileName: c.layers.intro?.originalVideoFileName || '',
          existingIntroVideoUrl: c.layers.intro?.video || '',
          chordsVideoFile: null,
          chordsVideoFileName: c.layers.chords?.originalVideoFileName || '',
          existingChordsVideoUrl: c.layers.chords?.video || '',
          melodyAudioFile: null,
          melodyAudioFileName: c.layers.chords?.originalMelodyAudioFileName || '',
          existingMelodyAudioUrl: c.layers.chords?.melodyAudio || '',
          chordsPdfFile: null,
          chordsPdfFileName: c.layers.chords?.originalChordsPdfFileName || '',
          existingChordsPdfUrl: c.layers.chords?.chordsPdf || '',
        }))
      );
    }
  }, [existingCourse]);

  // Auto-generate slug from title
  useEffect(() => {
    if (!slugLocked && courseTitle) {
      setCourseSlug(slugify(courseTitle));
    }
  }, [courseTitle, slugLocked]);

  // ── Capsule management ──

  const addCapsule = useCallback(() => {
    setCapsules((prev) => [
      ...prev,
      {
        id: generateId(),
        slug: '',
        title: '',
        artist: '',
        introDescription: '',
        introVideoFile: null,
        introVideoFileName: '',
        existingIntroVideoUrl: '',
        chordsVideoFile: null,
        chordsVideoFileName: '',
        existingChordsVideoUrl: '',
        melodyAudioFile: null,
        melodyAudioFileName: '',
        existingMelodyAudioUrl: '',
        chordsPdfFile: null,
        chordsPdfFileName: '',
        existingChordsPdfUrl: '',
      },
    ]);
    setEditingCapsuleIndex(capsules.length);
    setStep('edit-capsule');
  }, [capsules.length]);

  const removeCapsule = useCallback((index: number) => {
    setCapsules((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const moveCapsule = useCallback((index: number, direction: -1 | 1) => {
    setCapsules((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }, []);

  const updateCapsule = useCallback((index: number, updates: Partial<CapsuleWorkingState>) => {
    setCapsules((prev) => prev.map((c, i) => (i === index ? { ...c, ...updates } : c)));
  }, []);

  // Auto-slug for capsule — always regenerate slug from title unless user manually edited it
  const capsuleSlugManuallyEdited = useRef<Set<string>>(new Set());

  const updateCapsuleTitle = useCallback((index: number, title: string) => {
    const c = capsules[index];
    const autoSlug = !capsuleSlugManuallyEdited.current.has(c.id);
    updateCapsule(index, {
      title,
      ...(autoSlug && { slug: slugify(title) }),
    });
  }, [capsules, updateCapsule]);

  // ── File handling ──

  const handleVideoSelect = useCallback(
    (index: number, layer: 'intro' | 'chords', file: File) => {
      if (layer === 'intro') {
        updateCapsule(index, { introVideoFile: file, introVideoFileName: file.name });
      } else {
        updateCapsule(index, { chordsVideoFile: file, chordsVideoFileName: file.name });
      }
    },
    [updateCapsule]
  );

  // ── Upload + Save ──

  const uploadFileIfNeeded = useCallback(
    async (file: File | null, existingUrl: string, folder: string, progressKey: string): Promise<string> => {
      if (!file) return existingUrl;
      if (!shouldUseCloudinary()) {
        // Local dev: just return a placeholder (no local upload server for courses)
        return existingUrl || `local://${file.name}`;
      }

      setUploadProgress((prev) => ({ ...prev, [progressKey]: 0 }));
      const result = await retryUpload(
        () =>
          uploadToCloudinary(file, folder, (progress) => {
            setUploadProgress((prev) => ({ ...prev, [progressKey]: progress.percent }));
          }),
        3
      );
      setUploadProgress((prev) => ({ ...prev, [progressKey]: 100 }));
      return result.secureUrl;
    },
    []
  );

  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaveError(null);

    try {
      // Validate capsule slugs before saving
      const slugs = capsules.map((c) => c.slug);
      const emptySlugs = slugs.filter((s) => !s.trim());
      if (emptySlugs.length > 0) {
        throw new Error('Some lessons have empty slugs. Please set a title or slug for each lesson.');
      }
      const duplicateSlugs = slugs.filter((s, i) => slugs.indexOf(s) !== i);
      if (duplicateSlugs.length > 0) {
        throw new Error(`Duplicate lesson slugs found: ${[...new Set(duplicateSlugs)].join(', ')}. Each lesson must have a unique slug.`);
      }

      // Upload all files
      const capsuleData: Array<{
        id: string;
        slug: string;
        title: string;
        artist?: string;
        order: number;
        layers: { intro?: CourseLayerContent; chords?: CourseLayerContent };
      }> = [];

      for (let i = 0; i < capsules.length; i++) {
        const c = capsules[i];
        const folder = `fingerstyle/courses/${courseSlug}/${c.slug}`;

        const introVideoUrl = await uploadFileIfNeeded(
          c.introVideoFile,
          c.existingIntroVideoUrl,
          folder,
          `intro-${i}`
        );

        const chordsVideoUrl = await uploadFileIfNeeded(
          c.chordsVideoFile,
          c.existingChordsVideoUrl,
          folder,
          `chords-${i}`
        );

        const melodyAudioUrl = await uploadFileIfNeeded(
          c.melodyAudioFile,
          c.existingMelodyAudioUrl,
          folder,
          `melody-${i}`
        );

        const chordsPdfUrl = await uploadFileIfNeeded(
          c.chordsPdfFile,
          c.existingChordsPdfUrl,
          folder,
          `pdf-${i}`
        );

        const layers: { intro?: CourseLayerContent; chords?: CourseLayerContent } = {};

        if (c.introDescription || introVideoUrl) {
          layers.intro = {
            ...(introVideoUrl && { video: introVideoUrl }),
            ...(introVideoUrl && c.introVideoFileName && { originalVideoFileName: c.introVideoFileName }),
            ...(c.introDescription && { description: c.introDescription }),
          };
        }

        if (chordsVideoUrl || melodyAudioUrl || chordsPdfUrl) {
          layers.chords = {
            ...(chordsVideoUrl && { video: chordsVideoUrl }),
            ...(chordsVideoUrl && c.chordsVideoFileName && { originalVideoFileName: c.chordsVideoFileName }),
            ...(melodyAudioUrl && { melodyAudio: melodyAudioUrl }),
            ...(melodyAudioUrl && c.melodyAudioFileName && { originalMelodyAudioFileName: c.melodyAudioFileName }),
            ...(chordsPdfUrl && { chordsPdf: chordsPdfUrl }),
            ...(chordsPdfUrl && c.chordsPdfFileName && { originalChordsPdfFileName: c.chordsPdfFileName }),
          };
        }

        capsuleData.push({
          id: c.id,
          slug: c.slug,
          title: c.title,
          ...(c.artist && { artist: c.artist }),
          order: i,
          layers,
        });
      }

      const response = await fetch('/api/capsules/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          contentType: 'course',
          slug: courseSlug,
          isNew: !existingCourse,
          title: courseTitle,
          ...(courseDescription && { description: courseDescription }),
          ...(courseArtist && { artist: courseArtist }),
          capsules: capsuleData,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save course');
      }

      await refetch();
      alert(`Course "${courseTitle}" saved successfully!`);

      if (!existingCourse) {
        navigate('/admin/courses');
      }
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  }, [capsules, courseSlug, courseTitle, courseDescription, courseArtist, existingCourse, navigate, refetch, uploadFileIfNeeded]);

  // ── Validation ──

  const isMetadataValid = courseTitle.trim() && courseSlug.trim() && /^[a-z0-9-]+$/.test(courseSlug);
  const isCapsuleValid = (c: CapsuleWorkingState) => c.title.trim() && c.slug.trim();

  // ── Render ──

  const editingCapsule = editingCapsuleIndex !== null ? capsules[editingCapsuleIndex] : null;

  return (
    <>
      <Header />
      <div className="container" style={{ paddingTop: 'var(--space-xl)', paddingBottom: 'var(--space-2xl)', maxWidth: 800 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 'var(--space-lg)' }}>
          {existingCourse ? `Edit: ${existingCourse.title}` : 'New Course'}
        </h1>

        {/* Step indicator */}
        {step !== 'edit-capsule' && (
          <div style={{ display: 'flex', gap: 'var(--space-sm)', marginBottom: 'var(--space-xl)' }}>
            {STEPS.map((s) => (
              <button
                key={s.key}
                className={`tab ${step === s.key ? 'active' : ''}`}
                onClick={() => setStep(s.key)}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}

        {/* ── Step 1: Course Metadata ── */}
        {step === 'metadata' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
            <label>
              <span style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Course Title *</span>
              <input className="input" value={courseTitle} onChange={(e) => setCourseTitle(e.target.value)} placeholder="e.g., קורס ליווי שירי מתי כספי" />
            </label>
            <label>
              <span style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Slug *</span>
              <input
                className="input"
                value={courseSlug}
                onChange={(e) => { setCourseSlug(e.target.value); setSlugLocked(true); }}
                disabled={!!existingCourse}
                style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}
              />
              <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>URL: /courses/{courseSlug || '...'}</span>
            </label>
            <label>
              <span style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Artist</span>
              <input className="input" value={courseArtist} onChange={(e) => setCourseArtist(e.target.value)} placeholder="e.g., מתי כספי" />
            </label>
            <label>
              <span style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Description</span>
              <textarea
                className="input"
                rows={3}
                value={courseDescription}
                onChange={(e) => setCourseDescription(e.target.value)}
                placeholder="Course description..."
              />
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--space-md)' }}>
              <button className="btn btn-primary" onClick={() => setStep('capsules')} disabled={!isMetadataValid}>
                Next: Lessons
              </button>
            </div>
          </div>
        )}

        {/* ── Step 2: Capsule List ── */}
        {step === 'capsules' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-md)' }}>
              <h2 style={{ fontSize: 18, fontWeight: 600 }}>Lessons ({capsules.length})</h2>
              <button className="btn btn-sm btn-primary" onClick={addCapsule}>+ Add Lesson</button>
            </div>

            {capsules.length === 0 && (
              <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: 'var(--space-xl)' }}>
                No lessons yet. Click "Add Lesson" to start.
              </p>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
              {capsules.map((c, i) => (
                <div key={c.id} className="card">
                  <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <button className="btn btn-icon btn-icon-sm btn-ghost" onClick={() => moveCapsule(i, -1)} disabled={i === 0} title="Move up">▲</button>
                      <button className="btn btn-icon btn-icon-sm btn-ghost" onClick={() => moveCapsule(i, 1)} disabled={i === capsules.length - 1} title="Move down">▼</button>
                    </div>
                    <span style={{ fontWeight: 700, color: 'var(--color-text-muted)', width: 24, textAlign: 'center' }}>{i + 1}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600 }}>{c.title || '(untitled)'}</div>
                      <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                        {c.chordsVideoFileName ? `Video: ${c.chordsVideoFileName}` : c.existingChordsVideoUrl ? 'Video uploaded' : 'No video'}
                      </div>
                    </div>
                    <button className="btn btn-sm btn-secondary" onClick={() => { setEditingCapsuleIndex(i); setStep('edit-capsule'); }}>Edit</button>
                    <button className="btn btn-sm btn-ghost" onClick={() => removeCapsule(i)} style={{ color: 'var(--color-incorrect)' }}>Remove</button>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 'var(--space-xl)' }}>
              <button className="btn btn-secondary" onClick={() => setStep('metadata')}>Back</button>
              <button className="btn btn-primary" onClick={() => setStep('preview')} disabled={capsules.length === 0}>
                Next: Preview
              </button>
            </div>
          </div>
        )}

        {/* ── Step 2b: Edit Single Capsule ── */}
        {step === 'edit-capsule' && editingCapsule && editingCapsuleIndex !== null && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 style={{ fontSize: 18, fontWeight: 600 }}>
                Edit Lesson #{editingCapsuleIndex + 1}
              </h2>
              <button className="btn btn-sm btn-secondary" onClick={() => { setEditingCapsuleIndex(null); setStep('capsules'); }}>
                ← Back to list
              </button>
            </div>

            <label>
              <span style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Title *</span>
              <input className="input" value={editingCapsule.title} onChange={(e) => updateCapsuleTitle(editingCapsuleIndex, e.target.value)} placeholder="Song title" />
            </label>
            <label>
              <span style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Slug</span>
              <input className="input" value={editingCapsule.slug} onChange={(e) => { capsuleSlugManuallyEdited.current.add(editingCapsule.id); updateCapsule(editingCapsuleIndex, { slug: e.target.value }); }} style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }} />
              {!editingCapsule.slug && <span style={{ fontSize: 11, color: 'var(--color-incorrect)' }}>Slug is required — will be auto-generated from title</span>}
            </label>
            <label>
              <span style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Artist</span>
              <input className="input" value={editingCapsule.artist} onChange={(e) => updateCapsule(editingCapsuleIndex, { artist: e.target.value })} />
            </label>

            {/* Intro section */}
            <fieldset style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: 'var(--space-md)' }}>
              <legend style={{ fontSize: 14, fontWeight: 600, padding: '0 var(--space-sm)' }}>הקדמה (Intro)</legend>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
                <label>
                  <span style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>Description</span>
                  <textarea
                    className="input"
                    rows={3}
                    value={editingCapsule.introDescription}
                    onChange={(e) => updateCapsule(editingCapsuleIndex, { introDescription: e.target.value })}
                    placeholder="Intro text for the lesson..."
                  />
                </label>
                <div>
                  <span style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>Intro Video</span>
                  {(editingCapsule.introVideoFileName || editingCapsule.existingIntroVideoUrl) && (
                    <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 4 }}>
                      Current: {editingCapsule.introVideoFileName || 'uploaded'}
                    </p>
                  )}
                  <input
                    type="file"
                    accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleVideoSelect(editingCapsuleIndex, 'intro', file);
                    }}
                  />
                </div>
              </div>
            </fieldset>

            {/* Chords section */}
            <fieldset style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: 'var(--space-md)' }}>
              <legend style={{ fontSize: 14, fontWeight: 600, padding: '0 var(--space-sm)' }}>ליווי (Chords Video)</legend>
              <div>
                {(editingCapsule.chordsVideoFileName || editingCapsule.existingChordsVideoUrl) && (
                  <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 4 }}>
                    Current: {editingCapsule.chordsVideoFileName || 'uploaded'}
                  </p>
                )}
                <input
                  type="file"
                  accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleVideoSelect(editingCapsuleIndex, 'chords', file);
                  }}
                />
                <p style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4 }}>
                  Large files supported — uploaded in chunks via Cloudinary.
                </p>
              </div>
            </fieldset>

            {/* Practice materials */}
            <fieldset style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: 'var(--space-md)' }}>
              <legend style={{ fontSize: 14, fontWeight: 600, padding: '0 var(--space-sm)' }}>תרגול (Practice Materials)</legend>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
                <div>
                  <span style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>Melody Audio (WAV/MP3)</span>
                  <p style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 4 }}>
                    Background melody track for student practice.
                  </p>
                  {(editingCapsule.melodyAudioFileName || editingCapsule.existingMelodyAudioUrl) && (
                    <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 4 }}>
                      Current: {editingCapsule.melodyAudioFileName || 'uploaded'}
                    </p>
                  )}
                  <input
                    type="file"
                    accept="audio/wav,audio/mpeg,audio/mp3,audio/mp4,.wav,.mp3,.m4a"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) updateCapsule(editingCapsuleIndex, { melodyAudioFile: file, melodyAudioFileName: file.name });
                    }}
                  />
                </div>
                <div>
                  <span style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>Chords PDF</span>
                  <p style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 4 }}>
                    Chord progression sheet for viewing during practice.
                  </p>
                  {(editingCapsule.chordsPdfFileName || editingCapsule.existingChordsPdfUrl) && (
                    <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 4 }}>
                      Current: {editingCapsule.chordsPdfFileName || 'uploaded'}
                    </p>
                  )}
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) updateCapsule(editingCapsuleIndex, { chordsPdfFile: file, chordsPdfFileName: file.name });
                    }}
                  />
                </div>
              </div>
            </fieldset>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--space-md)' }}>
              <button
                className="btn btn-primary"
                onClick={() => { setEditingCapsuleIndex(null); setStep('capsules'); }}
                disabled={!isCapsuleValid(editingCapsule)}
              >
                Done
              </button>
            </div>
          </div>
        )}

        {/* ── Step 3: Preview & Save ── */}
        {step === 'preview' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
            <div className="card">
              <div className="card-body">
                <h2 style={{ fontSize: 20, fontWeight: 700 }}>{courseTitle}</h2>
                {courseArtist && <p style={{ color: 'var(--color-text-secondary)' }}>{courseArtist}</p>}
                {courseDescription && <p style={{ fontSize: 13, color: 'var(--color-text-muted)', marginTop: 'var(--space-sm)' }}>{courseDescription}</p>}
                <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 'var(--space-sm)', fontFamily: 'var(--font-mono)' }}>
                  /courses/{courseSlug}
                </p>
              </div>
            </div>

            <h3 style={{ fontSize: 16, fontWeight: 600 }}>Lessons ({capsules.length})</h3>
            {capsules.map((c, i) => (
              <div key={c.id} className="card">
                <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
                  <span style={{ fontWeight: 700, color: 'var(--color-accent)' }}>{i + 1}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{c.title}</div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                      {[
                        c.introDescription && 'intro text',
                        (c.introVideoFile || c.existingIntroVideoUrl) && 'intro video',
                        (c.chordsVideoFile || c.existingChordsVideoUrl) && 'chords video',
                        (c.melodyAudioFile || c.existingMelodyAudioUrl) && 'melody audio',
                        (c.chordsPdfFile || c.existingChordsPdfUrl) && 'chords PDF',
                      ].filter(Boolean).join(' · ') || 'no content'}
                    </div>
                  </div>
                  {uploadProgress[`chords-${i}`] !== undefined && uploadProgress[`chords-${i}`] < 100 && (
                    <span style={{ fontSize: 12, color: 'var(--color-accent)' }}>
                      Uploading: {uploadProgress[`chords-${i}`]}%
                    </span>
                  )}
                </div>
              </div>
            ))}

            {saveError && (
              <div className="callout callout-warning">
                <div className="callout-title">Save Error</div>
                <div className="callout-text">{saveError}</div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 'var(--space-md)' }}>
              <button className="btn btn-secondary" onClick={() => setStep('capsules')}>Back</button>
              <button className="btn btn-primary btn-lg" onClick={handleSave} disabled={saving || capsules.length === 0}>
                {saving ? 'Saving...' : existingCourse ? 'Update Course' : 'Create Course'}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
